import { gold } from '@ant-design/colors';
import { WarningTwoTone } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { Alert, Badge, Button, Empty, List } from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { Link } from 'react-router';
import { useLabResultViewStore } from '../../../store/useLabResultViewStore.ts';
import { patientFullName } from '../../../utils/patient.ts';
import type { PagedBefundeListState } from './useBefundeListState.ts';
import { usePagedBefundeListStyles } from './usePagedBefundeListStyles.ts';

export const PagedBefundeList: React.FC<{
  state: PagedBefundeListState;
  outdatedBefundIds: string[];
  onNextPage: () => void;
}> = ({ state, outdatedBefundIds, onNextPage }) => {
  const styles = usePagedBefundeListStyles();
  const selectedId = useLabResultViewStore(state => state.selectedId);

  if (state.error) {
    return <Alert type="warning" title="Fehler beim Laden der Daten" />;
  }

  return (
    <>
      <List
        dataSource={state.loadedItems}
        loading={state.loading}
        locale={{ emptyText: <Empty description="Keine Befunde gefunden" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        renderItem={item => (
          <Link to={`/${item.id}`}>
            <List.Item key={item.id} className={styles.befundListItem(item.id === selectedId, item.labResultStatus)}>
              <List.Item.Meta
                title={
                  <div className={styles.leftRight}>
                    <div className={styles.name}>
                      <Markable tokens={state.searchTokens}>{patientFullName(item)}</Markable>
                    </div>
                    <div className={styles.rightItem}>
                      {item.countPathological > 0 ? (
                        <span>
                          {item.countPathological} <WarningTwoTone twoToneColor={gold[5]} title="pathologisch" />
                        </span>
                      ) : null}
                    </div>
                  </div>
                }
                description={
                  <div className={styles.leftRight}>
                    <div className={styles.orderSvnr}>
                      <Markable tokens={state.searchTokens}>{item.orderNumber}</Markable>
                      {' ('}
                      <Markable tokens={state.searchTokens}>{item.svnr}</Markable>
                      {')'}
                    </div>
                    <div className={styles.rightItem}>
                      {item.unread || outdatedBefundIds.includes(item.id) ? (
                        <>
                          <Badge dot status="processing" />{' '}
                        </>
                      ) : null}
                      {formatTimestamp(item.timestamp)}
                    </div>
                  </div>
                }
              />
            </List.Item>
          </Link>
        )}
      />

      {state.hasMore && (
        <Button
          type="link"
          onClick={() => onNextPage()}
          className={css`
            width: 100%;
          `}
        >
          Weitere Befunde laden
        </Button>
      )}
    </>
  );
};

const formatTimestamp = (timestamp: string) => {
  const ts = dayjs(timestamp);
  const now = dayjs();
  if (ts.day() === now.day() && ts.month() === now.month() && ts.year() === now.year()) {
    return ts.format('HH:mm');
  }
  if (ts.day() === now.day() - 1 && ts.month() === now.month() && ts.year() === now.year()) {
    return 'Gestern';
  }
  return ts.format('DD.MM.YYYY');
};
